import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProFeatureDialog } from '@/components/ProFeatureDialog';
import { PublishToYouTubeDialog } from '@/components/publishing/PublishToYouTubeDialog';
import { PublishToInstagramDialog } from '@/components/publishing/PublishToInstagramDialog';
import { PublishToFacebookDialog } from '@/components/publishing/PublishToFacebookDialog';
import { TikTokPublishDialog } from '@/components/publishing/TikTokPublishDialog';
import type { PublishDialogTarget } from '@/lib/publishing/types';

interface Props {
  showPublishProGate: boolean;
  onPublishProGateChange: (open: boolean) => void;
  publishTarget: PublishDialogTarget | null;
  onPublishTargetChange: (target: PublishDialogTarget | null) => void;
  resolveClipTitle: (clipId: string) => string;
  /** Called after any provider accepts a publish request. */
  onPublished?: (target: PublishDialogTarget) => void;
}

/** One shared PRO gate and exactly one provider-specific dialog for a clip page. */
export const ClipPublishDialogs: React.FC<Props> = ({
  showPublishProGate,
  onPublishProGateChange,
  publishTarget,
  onPublishTargetChange,
  resolveClipTitle,
  onPublished,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const target = publishTarget;

  const clearTarget = () => {
    onPublishTargetChange(null);
  };
  const handlePublished = onPublished ?? (() => undefined);

  let clipTitle = '';
  if (target) {
    try {
      clipTitle = resolveClipTitle(target.clipId) || '';
    } catch {
      // The target may disappear during a refetch while its dialog is closing.
      clipTitle = '';
    }
  }

  return (
    <>
      <ProFeatureDialog
        open={showPublishProGate}
        onOpenChange={onPublishProGateChange}
        onUpgrade={() => navigate('/plans')}
        title={t('publishing.pro.title')}
        body={t('publishing.pro.body')}
        dismissLabel={t('publishing.pro.dismiss')}
        upgradeLabel={t('publishing.pro.upgrade')}
        testIdPrefix="publish-pro"
      />
      {target?.platform === 'tiktok' && (
        <TikTokPublishDialog
          key={`tiktok:${target.clipId}`}
          open
          onOpenChange={(open) => !open && clearTarget()}
          clipId={target.clipId}
          clipTitle={clipTitle}
          initialWhen={target.initialWhen}
          initialDatetimeLocal={target.initialDatetimeLocal}
          onSent={handlePublished}
        />
      )}
      {target?.platform === 'youtube' && (
        <PublishToYouTubeDialog
          key={`youtube:${target.clipId}`}
          open
          onOpenChange={(open) => !open && clearTarget()}
          clipId={target.clipId}
          clipTitle={clipTitle}
          initialWhen={target.initialWhen}
          initialDatetimeLocal={target.initialDatetimeLocal}
          onPublished={handlePublished}
        />
      )}
      {target?.platform === 'instagram' && (
        <PublishToInstagramDialog
          key={`instagram:${target.clipId}`}
          open
          onOpenChange={(open) => !open && clearTarget()}
          clipId={target.clipId}
          clipTitle={clipTitle}
          initialWhen={target.initialWhen}
          initialDatetimeLocal={target.initialDatetimeLocal}
          onPublished={handlePublished}
        />
      )}
      {target?.platform === 'facebook' && (
        <PublishToFacebookDialog
          key={`facebook:${target.clipId}`}
          open
          onOpenChange={(open) => !open && clearTarget()}
          clipId={target.clipId}
          clipTitle={clipTitle}
          initialWhen={target.initialWhen}
          initialDatetimeLocal={target.initialDatetimeLocal}
          onPublished={handlePublished}
        />
      )}
    </>
  );
};

export default ClipPublishDialogs;
